// Cliente HTTP de los documentos (PP-23). Mismo caller que la nomina: el
// cuerpo que sale y la respuesta que entra pasan por @ucorp/contracts.
//
// Aislamiento entre proveedores: la API responde 403 a un sujeto de otra
// empresa (ADR-014 §2). El cliente lo repite antes de llamar, para que una
// pantalla con datos viejos nunca mande una escritura ajena, y descarta del
// listado cualquier documento de otro proveedor aunque la respuesta lo traiga.
import {
  CreateDocumentRequest,
  Document,
  DocumentListResponse,
  UpdateDocumentRequest,
} from "@ucorp/contracts";
import { parseOrFail, sitePath, type FleetCall } from "../call.js";
import { FleetError, type FleetSite } from "../port.js";
import type { DocumentsPort } from "./port.js";

export function createHttpDocumentsPort(call: FleetCall): DocumentsPort {
  /** Un documento de otro proveedor en una respuesta de escritura es una respuesta rota. */
  function own(s: FleetSite, document: Document): Document {
    if (document.subject.providerId !== s.providerId) throw new FleetError("unavailable");
    return document;
  }

  return {
    async listDocuments(session, s, filter) {
      const query = new URLSearchParams({ providerId: s.providerId });
      if (filter !== undefined) {
        query.set("subjectType", filter.subjectType);
        query.set("subjectId", filter.subjectId);
      }
      const path = `${sitePath(s)}/documents?${query.toString()}`;
      const data = parseOrFail(DocumentListResponse, await call(session, "GET", path), "unavailable");
      return {
        today: data.today,
        documents: data.documents.filter((d) => d.subject.providerId === s.providerId),
        requiredDocuments: data.requiredDocuments,
      };
    },
    async createDocument(session, s, body) {
      if (body.subject.providerId !== s.providerId) throw new FleetError("access");
      const payload = parseOrFail(CreateDocumentRequest, body, "validation");
      return own(s, parseOrFail(Document, await call(session, "POST", `${sitePath(s)}/documents`, payload), "unavailable"));
    },
    async updateDocument(session, s, documentId, body) {
      const payload = parseOrFail(UpdateDocumentRequest, body, "validation");
      const path = `${sitePath(s)}/documents/${encodeURIComponent(documentId)}`;
      return own(s, parseOrFail(Document, await call(session, "PATCH", path, payload), "unavailable"));
    },
  };
}
